import { FC, ReactNode } from 'react'
import {
  Box, Button, Chip, Divider, Drawer, IconButton, Typography,
} from '@mui/material'
import { MdClose, MdOpenInNew } from 'react-icons/md'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IFlowManagementCardSelection } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { isItemArtApproved, isOrderWithPendingArts } from '../../../Services/HCP/flows/hcp-flow-card.helpers'
import { getFlowHistoryActorLabel } from '../../../Services/HCP/flows/hcp-flow-history.helpers'
import { HCPFlowEnum } from '../../../Services/HCP/flows/hcp-flow.interface'

interface IFlowManagementCardDrawerProps {
  selection: IFlowManagementCardSelection | null
  onClose: () => void
}

function formatDate(value?: string | null, withTime = true): string {
  if (!value) return '—'
  const date = parseISO(value)
  if (!isValid(date)) return value
  return format(date, withTime ? "dd/MM/yyyy 'às' HH:mm" : 'dd/MM/yyyy', { locale: ptBR })
}

function formatQuantity(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('pt-BR')
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}
    >
      {children}
    </Typography>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

const FlowManagementCardDrawer: FC<IFlowManagementCardDrawerProps> = ({ selection, onClose }) => {
  const card = selection?.card
  const flow = card?.hcpCurrentFlow as HCPFlowEnum | undefined
  const isLogisticsOrderCard = Boolean(card?.isLogisticsOrderCard)
  const logisticsItems = card?.logisticsOrderItems ?? []
  const showArtChips = flow === HCPFlowEnum.AWAITING_ART || flow === HCPFlowEnum.SHEET
  const itemArtApproved = card ? isItemArtApproved(card, flow) : false
  const orderWithPendingArts = card ? isOrderWithPendingArts(card) : false
  const displayCode = card?.displayCode ?? card?.hcpCurrentFlowDisplayCode
  const statusName = card?.hcpCurrentFlowStatusName ?? selection?.columnName ?? '—'

  const orderTitle = card?.order?.nsOrderId
    ? `Pedido ${card.order.nsOrderId}${card.productOrderCode ? ` ${card.productOrderCode}` : ''}`
    : `Item ${card?.id ?? '—'}`

  return (
    <Drawer
      anchor="right"
      open={selection !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: 440, display: 'flex', flexDirection: 'column' } }}
    >
      {card && (
        <>
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} noWrap title={orderTitle}>
                  {orderTitle}
                </Typography>
                <Typography variant="body2" fontWeight={500} noWrap title={card.order?.customer?.name ?? undefined}>
                  {card.order?.customer?.name ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {card.product?.name ?? '—'}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} sx={{ ml: 1, flexShrink: 0 }}>
                <MdClose size={20} />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
              {showArtChips && itemArtApproved && (
                <Chip
                  label="Arte aprovada"
                  size="small"
                  sx={{ bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {showArtChips && !itemArtApproved && (
                <Chip
                  label="Arte pendente"
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {showArtChips && itemArtApproved && orderWithPendingArts && flow === HCPFlowEnum.SHEET && (
                <Chip
                  label="Outros itens do pedido pendentes"
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {card.hcpFinished && (
                <Chip
                  label="Finalizado no fluxo"
                  size="small"
                  sx={{ bgcolor: 'rgba(0,0,0,0.06)', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {card.serviceOrderId && (
                <Chip
                  label={`OS #${card.serviceOrderId}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <SectionTitle>Status no fluxo</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="Etapa" value={statusName} />
                <InfoItem label="Código" value={displayCode ?? '—'} />
                <InfoItem label="Sequência" value={String(card.hcpCurrentFlowStatusSequence || selection?.columnSequence || '—')} />
                <InfoItem label="Fluxo" value={card.hcpCurrentFlow || '—'} />
              </Box>
            </Box>

            <Divider />

            <Box>
              <SectionTitle>{isLogisticsOrderCard ? 'Itens do pedido' : 'Produto'}</SectionTitle>
              {isLogisticsOrderCard ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {logisticsItems.map((item) => (
                    <Box
                      key={String(item.id)}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,0,0,0.03)',
                        border: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                        {item.productOrderCode ? `${item.productOrderCode} · ` : ''}{item.productName ?? 'Item'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[
                          item.volumetry,
                          item.totalQuantity != null ? `Qtd ${formatQuantity(item.totalQuantity)}` : null,
                          item.displayCode ? `Status ${item.displayCode}` : null,
                          item.serviceOrderId ? `OS ${item.serviceOrderId}` : null,
                        ].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <InfoItem label="Nome" value={card.product?.name ?? '—'} />
                  <InfoItem label="Volumetria" value={card.volumetry ?? '—'} />
                  <InfoItem label="Quantidade" value={formatQuantity(card.totalQuantity)} />
                  <InfoItem label="Cód. produto" value={card.product?.code ?? '—'} />
                  <InfoItem label="ID produto" value={card.product?.id ? String(card.product.id) : '—'} />
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <SectionTitle>Prazos</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="Prazo do cliente" value={formatDate(card.customerDeadline)} />
                <InfoItem label="Prazo do fluxo HCP" value={formatDate(card.hcpCurrentFlowDeadline)} />
                <InfoItem label="Início no fluxo" value={formatDate(card.hcpCurrentFlowStartedAt)} />
                <InfoItem label="Criado em" value={formatDate(card.createdAt)} />
                <InfoItem label="Atualizado em" value={formatDate(card.updatedAt)} />
              </Box>
            </Box>

            <Divider />

            <Box>
              <SectionTitle>Pedido e vínculos</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="ID do item" value={isLogisticsOrderCard ? 'Card do pedido' : String(card.id)} />
                <InfoItem label="Posição no pedido" value={card.productOrderCode ?? '—'} />
                <InfoItem label="ID do pedido" value={card.order?.id ? String(card.order.id) : '—'} />
                <InfoItem label="Nº pedido Nuvem" value={card.order?.nsOrderId ? String(card.order.nsOrderId) : '—'} />
                <InfoItem label="E-mail do cliente" value={card.order?.customer?.email ?? '—'} />
                <InfoItem label="OS" value={card.serviceOrderId ? String(card.serviceOrderId) : '—'} />
              </Box>
            </Box>

            {card.artLink && (
              <>
                <Divider />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdOpenInNew size={15} />}
                  onClick={() => window.open(card.artLink!, '_blank', 'noopener,noreferrer')}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                >
                  Abrir link da arte
                </Button>
              </>
            )}

            {card.hcpFlowHistories && card.hcpFlowHistories.length > 0 && (
              <>
                <Divider />
                <Box>
                  <SectionTitle>Histórico do fluxo</SectionTitle>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {card.hcpFlowHistories.map((history, index) => {
                      const actorLabel = getFlowHistoryActorLabel(history)

                      return (
                      <Box
                        key={`${history.displayCode ?? history.sequence ?? index}-${index}`}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(0,0,0,0.03)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                          {history.statusName ?? history.flow ?? 'Movimentação'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {[
                            history.displayCode ? `Código ${history.displayCode}` : null,
                            history.sequence ? `Seq. ${history.sequence}` : null,
                            history.flow ? `Fluxo ${history.flow}` : null,
                          ].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                        {actorLabel && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Por {actorLabel}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(history.createdAt)}
                        </Typography>
                      </Box>
                      )
                    })}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </>
      )}
    </Drawer>
  )
}

export default FlowManagementCardDrawer
